export default class Api {
async checkResponse(response) {
    if (!response.ok) {
        if (response.status === 401) {
            localStorage.removeItem("authToken");
            localStorage.removeItem("authTokenExpiresAt");
            // Only redirect if we're not already on the login page
            if (!window.location.pathname.startsWith('/login')) {
                window.location.href = "/login?expired=true";
            }
            throw new Error("Session expired");
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response;
}

getHeaders(boardId) {
    const jwtToken = localStorage.getItem("authToken");
    const headers = new Headers();
    headers.append("Accept", "application/json");
    headers.append("Content-Type", "application/json");

    if (jwtToken) {
        headers.append("Authorization", "Bearer " + jwtToken);
    }

    if (boardId) {
        headers.append("x-board-id", boardId);
    }

    return headers;
}

async get(uri, boardId) {
    const headers = this.getHeaders(boardId);
    const response = await fetch(uri, {
        headers,
        method: "GET",
        mode: "cors",
    });
    return this.checkResponse(response);
}

async delete(uri, boardId) {
    const headers = this.getHeaders(boardId);
    const response = await fetch(uri, {
        headers,
        method: "DELETE",
        mode: "cors",
    });
    return this.checkResponse(response);
}

async post(uri, body = "", boardId) {
    const headers = this.getHeaders(boardId);
    const response = await fetch(uri, {
        body: JSON.stringify(body),
        headers,
        method: "POST",
        mode: "cors",
    });
    return this.checkResponse(response);
}

async put(uri, body = "", boardId) {
    const headers = this.getHeaders(boardId);
    const response = await fetch(uri, {
        body: JSON.stringify(body),
        headers,
        method: "PUT",
        mode: "cors",
    });
    return this.checkResponse(response);
}


async getJSON(uri, boardId) {
    const response = await this.get(uri, boardId);
    return response.json();
}

async postJSON(uri, body, boardId) {
    const response = await this.post(uri, body, boardId);
    // eslint-disable-next-line no-return-await
    return await response.json();
}

async putJSON(uri, body, boardId) {
    const response = await this.put(uri, body, boardId);
    // eslint-disable-next-line no-return-await
    return await response.json();
}

async deleteJSON(uri, body, boardId) {
    const response = await this.delete(uri, boardId);
    // eslint-disable-next-line no-return-await
    return await response.json();
}

async getPricingInfo(companyId) {
    return this.getJSON(`/api/v1/companies/${companyId}/pricing`);
}

async register(email, password, company, ref) {
    const headers = new Headers();
    headers.append("Accept", "application/json");
    headers.append("Content-Type", "application/json");
    const response = await fetch(`/api/v1/registrations`, {
        body: JSON.stringify(
            {
                email: email,
                password: password,
                company: company,
                ref: ref,
            }
        ),
        headers,
        method: "POST",
        mode: "cors",
    });
    const checkedResponse = await this.checkResponse(response);
    return checkedResponse.json();
}

async login(email, password) {
    const headers = new Headers();
    headers.append("Accept", "application/json");
    headers.append("Content-Type", "application/json");
    const response = await fetch(`/api/v1/login`, {
        body: JSON.stringify(
            {
                email: email,
                password: password,
            }
        ),
        headers,
        method: "POST",
        mode: "cors",
    });
    const checkedResponse = await this.checkResponse(response);
    return checkedResponse.json();
}

async loginWithGoogle(googleToken) {
    const headers = new Headers();
    headers.append("Accept", "application/json");
    headers.append("Content-Type", "application/json");
    const response = await fetch(`/api/v1/login/google`, {
        body: JSON.stringify(
            {
                googleToken: googleToken,
            }
        ),
        headers,
        method: "POST",
        mode: "cors",
    });
    const checkedResponse = await this.checkResponse(response);
    return checkedResponse.json();
}

async logout() {
    return this.postJSON(`/api/v1/logout`);
}

async refreshToken() {
    return this.postJSON(`/api/v1/refresh-token`);
}

async passwordRecoverySendLink(data) {
    const response = await this.post(
        `/api/v1/password-recovery/send-link`,
        data
    );
    // eslint-disable-next-line no-return-await
    return await response;
}

async passwordRecoveryCheckToken(data) {
    return this.postJSON(`/api/v1/password-recovery/check-token`, data);
}

async passwordRecoverySetNew(email, password, token) {
    const response = await this.post(`/api/v1/password-recovery/new-password`, {
        email: email,
        newPasswordToken: token,
        password: password,
    });
    // eslint-disable-next-line no-return-await
    return await response;
}

async createNewBoard(data, companyId) {
    return this.postJSON(`/api/v1/companies/${companyId}/boards`, data);
}

async createNewRelease(data, companyId) {
    return this.postJSON(`/api/v1/companies/${companyId}/releases`, data);
}

async updateRelease(data, releaseId) {
    return this.putJSON(`/api/v1/releases/${releaseId}`, data);
}

async deleteReleasePart(partId, releaseId) {
    return this.deleteJSON(`/api/v1/releases/${releaseId}/parts/${partId}`);
}

async addGenericReleasePart(data, releaseId) {
    return this.postJSON(`/api/v1/releases/${releaseId}/parts`, data);
}

async addIdeasAsReleaseParts(data, releaseId) {
    return this.postJSON(`/api/v1/releases/${releaseId}/add-from-ideas`, data);
}

async changePublishRelease(data, releaseId) {
    return this.putJSON(`/api/v1/releases/${releaseId}/publish`, data);
}

async getUserInfo() {
    return this.getJSON(`/api/v1/me`);
}

async getUserProfile() {
    return this.getJSON(`/api/v1/profile`);
}

async getCompanyInfoById(companyId) {
    return this.getJSON(`/api/v1/company/${companyId}`);
}
async updateUserLanguage(data) {
    return this.putJSON(`/api/v1/profile/language`, data);
}

async changeUserPassword(currentPassword, newPassword) {
    return this.put(`/api/v1/profile/password`, {
        currentPassword: currentPassword,
        newPassword: newPassword,
    });
}

async getBoards(companyId) {
    return this.getJSON(`/api/v1/companies/${companyId}/boards`);
}

async getBoardDetails(boardId) {
    return this.getJSON(`/api/v1/boards/${boardId}`);
}

async getBoardLimits(boardId) {
    return this.getJSON(`/api/v1/boards/${boardId}/limits`);
}

async updateBoardName(data, boardId) {
    return this.putJSON(`/api/v1/boards/${boardId}/name`, data);
}

async updateBoardLanguage(data, boardId) {
    return this.putJSON(`/api/v1/boards/${boardId}/language`, data);
}

async updateBoardTheme(data, boardId) {
    return this.putJSON(`/api/v1/boards/${boardId}/theme`, data);
}

async updateBoardPrivacy(data, boardId) {
    return this.putJSON(`/api/v1/boards/${boardId}/set-public`, data);
}

async updateBoardShortDescription(data, boardId) {
    return this.putJSON(`/api/v1/boards/${boardId}/short-description`, data);
}

async getBoardReleases(boardId) {
    return this.getJSON(`/api/v1/boards/${boardId}/releases`);
}

async getWidgetSetup(boardId) {
    return this.getJSON(`/api/v1/boards/${boardId}/widget-setup`);
}

async getBoardIdeas(boardId) {
    return this.getJSON(`/api/v1/ideas/`, boardId);
}

async getIdea(ideaId, boardId) {
    return this.getJSON(`/api/v1/ideas/${ideaId}`, boardId);
}

async createNewIdea(data, boardId) {
    return this.postJSON(`/api/v1/ideas/`, data, boardId);
}

async editIdea(data, ideaId, boardId) {
    return this.putJSON(`/api/v1/ideas/${ideaId}`, data, boardId);
}

async editIdeaStatus(data, ideaId, boardId) {
    return this.putJSON(`/api/v1/ideas/${ideaId}/status`, data, boardId);
}

async declineIdea(data, ideaId, boardId) {
    return this.postJSON(`/api/v1/ideas/${ideaId}/decline`, data, boardId);
}

async deleteIdea(ideaId, boardId) {
    return this.deleteJSON(`/api/v1/ideas/${ideaId}`, {}, boardId);
}

async giveIdeaUpvote(ideaId, boardId) {
    return this.postJSON(`/api/v1/ideas/${ideaId}/upvote`, {}, boardId);
}

async giveIdeaDownvote(ideaId, boardId) {
    return this.postJSON(`/api/v1/ideas/${ideaId}/downvote`, {}, boardId);
}

async listVoters(ideaId, boardId) {
    return this.getJSON(`/api/v1/ideas/${ideaId}/voters`, boardId);
}

async getIdeaComments(ideaId, boardId) {
    return this.getJSON(`/api/v1/ideas/${ideaId}/comments`, boardId);
}

async addIdeaComment(data, ideaId, boardId) {
    return this.postJSON(`/api/v1/ideas/${ideaId}/comments`, data, boardId);
}

async getReleases(companyId) {
    return this.getJSON(`/api/v1/companies/${companyId}/releases`);
}

async getReleaseDetails(releaseId) {
    return this.getJSON(`/api/v1/releases/${releaseId}`);
}

async getUsers(companyId) {
    return this.getJSON(`/api/v1/companies/${companyId}/users`);
}

async getCompanyMembers(companyId) {
    return this.getJSON(`/api/v1/companies/${companyId}/members`);
}

async addCompanyMember(companyId, data) {
    return this.postJSON(`/api/v1/companies/${companyId}/members`, data);
}

async deactivateCompanyMember(companyId, userId) {
    return this.postJSON(`/api/v1/companies/${companyId}/members/${userId}/deactivate`);
}

async activateCompanyMember(companyId, userId) {
    return this.postJSON(`/api/v1/companies/${companyId}/members/${userId}/activate`);
}

async changeCompanyMemberApps(companyId, userId, apps) {
    return this.putJSON(`/api/v1/companies/${companyId}/members/${userId}/apps`, { apps });
}

async getCompanySubscription(companyId) {
    return this.getJSON(`/api/v1/companies/${companyId}/subscription`);
}

async upgradeCompanySubscription(companyId, data) {
    return this.postJSON(
        `/api/v1/companies/${companyId}/subscription/upgrade/`,
        data
    );
}

async getSmsAgents(companyId) {
    return this.getJSON(`/api/v1/companies/${companyId}/msg/sms/agents`);
}

async forgetSmsAgent(companyId, agentId) {
    return this.delete(`/api/v1/companies/${companyId}/msg/sms/agents/${agentId}`);
}

async getSmsJobs(companyId, page = 0, size = 5) {
    return this.getJSON(`/api/v1/companies/${companyId}/msg/sms/jobs?page=${page}&size=${size}`);
}

async createSmsJob(companyId, data) {
    return this.postJSON(`/api/v1/companies/${companyId}/msg/sms/jobs`, data);
}

async getSmsJobDetails(companyId, jobId) {
    return this.getJSON(`/api/v1/companies/${companyId}/msg/sms/jobs/${jobId}`);
}

async cancelSmsJob(companyId, jobId) {
    return this.post(`/api/v1/companies/${companyId}/msg/sms/jobs/${jobId}/cancel`);
}

async getAgentAuthQrCode() {
    return this.getJSON(`/api/v1/msg/sms/agents/auth-qrcode`);
}

async getContacts(companyId) {
    return this.getJSON(`/api/v1/companies/${companyId}/msg/contacts`);
}

async createContact(companyId, data) {
    return this.postJSON(`/api/v1/companies/${companyId}/msg/contacts`, data);
}

async updateContact(companyId, contactId, data) {
    return this.putJSON(`/api/v1/companies/${companyId}/msg/contacts/${contactId}`, data);
}

async deleteContact(companyId, contactId) {
    return this.delete(`/api/v1/companies/${companyId}/msg/contacts/${contactId}`);
}

async getContactTags(companyId) {
    return this.getJSON(`/api/v1/companies/${companyId}/msg/contacts/tags`);
}

async validateContactsImport(companyId, csvData) {
    return this.postJSON(`/api/v1/companies/${companyId}/msg/contacts/import/validate`, { csvData });
}

async executeContactsImport(companyId, csvData) {
    return this.postJSON(`/api/v1/companies/${companyId}/msg/contacts/import/execute`, { csvData });
}

async getSmsRecipientsCount(companyId, byTags = []) {
    return this.postJSON(`/api/v1/companies/${companyId}/msg/sms/recipients`, { byTags });
}

async getOnboarding(companyId) {
    return this.getJSON(`/api/v1/companies/${companyId}/onboarding`);
}

async updateOnboardingTask(companyId, task, done) {
    return this.putJSON(`/api/v1/companies/${companyId}/onboarding`, { task, done });
}

async setOnboardingDismissed(companyId, dismissed) {
    return this.putJSON(`/api/v1/companies/${companyId}/onboarding/dismissed`, { dismissed });
}

async getNotifications(companyId, page = 0, size = 25) {
    return this.getJSON(`/api/v1/companies/${companyId}/notifications?page=${page}&size=${size}`);
}

async markAllNotificationsAsRead(companyId, beforeNotificationId) {
    return this.post(`/api/v1/companies/${companyId}/notifications/mark-all-as-read`, {
        beforeNotificationId: beforeNotificationId
    });
}
}
